import { useMemo, useCallback } from "react";
import type { Book } from "../types";
import {
  parseCoverLayout,
  type BookCoverLayout,
  type CoverPlatform,
} from "../../shared/coverGeometry";
import type { PublishSettings } from "../../shared/publishFormattingShared";
import { estimateTotalPages } from "../../shared/publishFormattingShared";

/**
 * Derive cover layout from book metadata + live publish settings / page estimate.
 */
export function useCoverLayout(
  book: Book,
  settings: PublishSettings,
  pageCountOverride?: number
): {
  coverLayout: BookCoverLayout;
  persistCoverLayout: (next: BookCoverLayout, onBookChange: (u: Partial<Book>) => void) => void;
} {
  const coverLayout = useMemo(() => {
    const saved = (book.metadata as Record<string, unknown>)?.coverLayout;
    const base = parseCoverLayout(saved);
    const pageCount =
      typeof pageCountOverride === "number" && pageCountOverride > 0
        ? pageCountOverride
        : estimateTotalPages(book, settings);
    return parseCoverLayout({
      ...base,
      platform: (settings.platform === "ingramspark" ? "ingramspark" : "kdp-print") as CoverPlatform,
      trimSize: settings.trimSize,
      pageCount,
    });
  }, [book, settings, pageCountOverride]);

  const persistCoverLayout = useCallback(
    (next: BookCoverLayout, onBookChange: (u: Partial<Book>) => void) => {
      onBookChange({
        metadata: { coverLayout: next },
      });
    },
    []
  );

  return { coverLayout, persistCoverLayout };
}
